import { createTheme, ThemeOptions } from '@mui/material/styles';
import React from 'react';

// =============================================================================
// TYPOGRAPHY STACKS
// -----------------------------------------------------------------------------
// Mismas familias que la landing (LandingPage.tsx) para que el dashboard tenga
// la misma voz editorial. Se cargan desde public/index.html via Google Fonts.
// =============================================================================
// Fuente única de la app: Geist (la de cobrando.app). Display y body comparten
// familia; los títulos se distinguen por peso/tracking, no por familia.
export const DISPLAY_FONT = '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
export const BODY_FONT = '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
export const MONO_FONT = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

// =============================================================================
// PALETTE TOKENS
// -----------------------------------------------------------------------------
// Restyle "híbrido frío-limpio": tomamos la limpieza de Cobrando (índigo
// eléctrico, sidebar clara, sombras/bordes sutiles, píldoras) pero conservamos
// la ESENCIA propia → base en blanco CÁLIDO (no slate frío) + mono en datos +
// títulos en serif. El amarillo queda como acento puntual.
// =============================================================================
export const PAPER_WARM = '#F6F6F4';       // Blanco cálido — background.default (limpio, tibio)
export const PAPER_CARD = '#FFFFFF';       // Superficies/cards/sidebar/appbar
export const PAPER_TINT = '#F5F3EE';       // Tinte cálido para zebra/hover/heads de tabla
export const INK = '#0A0A0A';              // Negro tinta (texto principal)
export const INK_SOFT = '#1C1B1A';
export const INDIGO = '#384DF4';           // Primary — índigo eléctrico de Cobrando
export const INDIGO_DARK = '#2C3FD0';
export const INDIGO_LIGHT = '#5B6CF6';
export const YELLOW_ACCENT = '#F6D058';    // Acento puntual (no protagonista)
export const YELLOW_DEEP = '#E8B923';
export const RULE_SOFT = 'rgba(17,17,17,0.07)';   // Hairline cálido sutil
export const RULE_HARD = 'rgba(17,17,17,0.12)';   // Hairline un toque más marcado

// Compat: algunos módulos importan PAPER_CREAM. Lo mantenemos como alias cálido.
export const PAPER_CREAM = PAPER_TINT;

// =============================================================================
// MUI MODULE AUGMENTATION
// -----------------------------------------------------------------------------
// Habilita <Typography variant="mono"> y <Typography variant="display"> con
// type-safety completo en todo el proyecto.
// =============================================================================
declare module '@mui/material/styles' {
  interface TypographyVariants {
    mono: React.CSSProperties;
    display: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    mono?: React.CSSProperties;
    display?: React.CSSProperties;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    mono: true;
    display: true;
  }
}

// =============================================================================
// PALETTE
// =============================================================================
const colors = {
  primary: {
    main: INDIGO,        // índigo eléctrico (Cobrando)
    light: INDIGO_LIGHT,
    dark: INDIGO_DARK,
    contrastText: '#ffffff',
  },
  secondary: {
    main: YELLOW_ACCENT, // amarillo: acento puntual, ya no protagonista
    light: '#F9DF84',
    dark: YELLOW_DEEP,
    contrastText: INK,
  },
  background: {
    default: PAPER_WARM, // blanco cálido casi-neutro (limpio, conserva tibieza)
    paper: PAPER_CARD,   // cards/superficies en blanco
  },
  text: {
    primary: INK,
    secondary: '#51606E', // slate un toque más neutro
  },
  success: {
    main: '#16a34a',
    light: '#22c55e',
    dark: '#15803d',
  },
  warning: {
    main: '#d97706',
    light: '#f59e0b',
    dark: '#b45309',
  },
  error: {
    main: '#dc2626',
    light: '#ef4444',
    dark: '#b91c1c',
  },
  info: {
    main: '#2563eb',
    light: '#3b82f6',
    dark: '#1d4ed8',
  },
};

const neutral = {
  main: '#64748b',
  light: '#94a3b8',
  dark: '#475569',
};

// =============================================================================
// THEME
// =============================================================================
const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    ...colors,
  },
  typography: {
    fontFamily: BODY_FONT,
    // Headings en Geist: se distinguen por peso (600-700) y tracking apretado,
    // estilo cobrando.app.
    h1: {
      fontFamily: DISPLAY_FONT,
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.03em',
    },
    h2: {
      fontFamily: DISPLAY_FONT,
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.15,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontFamily: DISPLAY_FONT,
      fontSize: '1.625rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    // h4 es el page-title más usado.
    h4: {
      fontFamily: DISPLAY_FONT,
      fontSize: '1.75rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontFamily: BODY_FONT,
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontFamily: BODY_FONT,
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '-0.005em',
    },
    body1: {
      fontFamily: BODY_FONT,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: BODY_FONT,
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: BODY_FONT,
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '0.005em',
    },
    overline: {
      fontFamily: BODY_FONT,
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      fontSize: '0.7rem',
    },
    caption: {
      fontFamily: BODY_FONT,
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    // Variantes custom para datos/códigos (referencias, SKUs, IDs, fechas)
    mono: {
      fontFamily: MONO_FONT,
      fontSize: '0.85rem',
      fontFeatureSettings: '"tnum" 1, "ss01" 1',
      letterSpacing: '-0.01em',
    },
    display: {
      fontFamily: DISPLAY_FONT,
      fontSize: '3rem',
      fontWeight: 800,
      lineHeight: 1.05,
      letterSpacing: '-0.035em',
    },
  },
  shape: {
    borderRadius: 5,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: BODY_FONT,
          // Renderizado parejo entre Chrome/Safari/Firefox
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
        },
        // Hace que los números en tablas alineen vertical en cualquier slot
        'table, .mono-numerals': {
          fontVariantNumeric: 'tabular-nums',
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          mono: 'span',
          display: 'h1',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,   // píldora (estilo Cobrando)
          fontWeight: 500,
          textTransform: 'none',
          boxShadow: 'none',
          paddingInline: 18,
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(56,77,244,0.20)',
          },
        },
        outlined: {
          borderColor: RULE_HARD,
          '&:hover': {
            borderColor: 'rgba(17,17,17,0.24)',
            backgroundColor: 'rgba(17,17,17,0.03)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,    // rounded-xl (Cobrando)
          boxShadow: '0 1px 3px rgba(15,23,42,0.08), 0 1px 2px -1px rgba(15,23,42,0.06)',
          border: `1px solid ${RULE_SOFT}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
        },
        elevation4: {
          boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            fontFamily: BODY_FONT,
            backgroundColor: PAPER_CARD,
            '& fieldset': {
              borderColor: RULE_HARD,
            },
            '&:hover fieldset': {
              borderColor: 'rgba(17,17,17,0.22)',
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              borderWidth: 1.5,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontFamily: BODY_FONT,
          // Estados como píldora tenue: el color se lee sin gritar (los chips sólidos tapaban la tabla).
          '&.MuiChip-filled.MuiChip-colorDefault': { backgroundColor: '#F0F0EE', color: '#4A5361' },
          '&.MuiChip-filled.MuiChip-colorSuccess': { backgroundColor: '#E8F5EE', color: '#0F6B45' },
          '&.MuiChip-filled.MuiChip-colorWarning': { backgroundColor: '#FFF4DE', color: '#8A5200' },
          '&.MuiChip-filled.MuiChip-colorError': { backgroundColor: '#FDECEA', color: '#A8251B' },
          '&.MuiChip-filled.MuiChip-colorInfo': { backgroundColor: '#ECEEFE', color: '#2B3BB8' },
        },
        sizeSmall: {
          height: 22,
          fontSize: '0.78rem',
          fontWeight: 600,
          borderRadius: 999,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: PAPER_CARD,
          borderRight: `1px solid ${RULE_SOFT}`,
          overflowX: 'hidden',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: PAPER_CARD,
          color: colors.text.primary,
          boxShadow: 'none',
          borderBottom: `1px solid ${RULE_SOFT}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '& .MuiListItemIcon-root': {
            color: neutral.dark,
            minWidth: 36,
          },
          '&.Mui-selected': {
            backgroundColor: `${colors.primary.main}14`,
            color: colors.text.primary,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: `${colors.primary.main}1f`,
            },
            '& .MuiListItemIcon-root': {
              color: colors.primary.main,
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(17,17,17,0.04)',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F7F7F5',
          '& .MuiTableCell-head': {
            // Cabeceras en minúscula normal y gris: se leen de un vistazo (las mayúsculas en mono cansaban).
            fontWeight: 600,
            color: colors.text.secondary,
            fontFamily: BODY_FONT,
            fontSize: '0.78rem',
            letterSpacing: 0,
            whiteSpace: 'nowrap',
            borderBottom: `1px solid ${RULE_HARD}`,
            paddingTop: 10,
            paddingBottom: 10,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child > .MuiTableCell-body': { borderBottom: 'none' },
          '.MuiTableBody-root > &:hover': { backgroundColor: 'rgba(56,77,244,0.035)' },
          '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'rgba(56,77,244,0.07)' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        // Números/IDs/totales se ven más prolijos con tabular figures
        root: {
          fontVariantNumeric: 'tabular-nums',
          borderBottom: `1px solid ${RULE_SOFT}`,
          fontSize: '0.875rem',
        },
        sizeSmall: {
          paddingTop: 9,
          paddingBottom: 9,
        },
        body: {
          color: colors.text.primary,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40, borderBottom: `1px solid ${RULE_SOFT}` },
        indicator: { height: 2, borderRadius: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9rem',
          color: colors.text.secondary,
          paddingLeft: 4,
          paddingRight: 4,
          marginRight: 20,
          minWidth: 0,
          '&.Mui-selected': { color: INK, fontWeight: 600 },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          paddingTop: 4,
          paddingBottom: 4,
          borderColor: RULE_HARD,
          '&.Mui-selected': { backgroundColor: INK, color: '#fff', '&:hover': { backgroundColor: INK_SOFT } },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: `1px solid ${RULE_SOFT}`,
          boxShadow: '0 20px 60px rgba(15,23,42,0.16)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: DISPLAY_FONT,
          fontWeight: 600,
          fontSize: '1.25rem',
          letterSpacing: '-0.02em',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${RULE_SOFT}`,
          boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 5,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 4,
          fontFamily: BODY_FONT,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
};

export const DashFlowTheme = createTheme(themeOptions);

// =============================================================================
// DARK VARIANT
// =============================================================================
export const darkThemeOptions: ThemeOptions = {
  ...themeOptions,
  palette: {
    mode: 'dark',
    primary: {
      main: INDIGO_LIGHT,
      light: '#7E8BFF',
      dark: INDIGO,
      contrastText: '#ffffff',
    },
    secondary: {
      main: YELLOW_ACCENT,
      light: '#F9DF84',
      dark: YELLOW_DEEP,
      contrastText: INK,
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#cbd5e1',
    },
  },
};

export const DashFlowDarkTheme = createTheme(darkThemeOptions);

// =============================================================================
// EXPORTS adicionales
// =============================================================================
export const stockStatusColors = {
  OK: colors.success.main,
  WARNING: colors.warning.main,
  LOW: '#ff9800',
  CRITICAL: colors.error.main,
};

export const chartColors = [
  INDIGO,
  colors.secondary.main,
  colors.success.main,
  colors.warning.main,
  colors.error.main,
  colors.info.main,
  '#9c27b0',
  '#795548',
  '#607d8b',
  '#e91e63',
];
